import React, { useCallback, useEffect, useState } from 'react';

import { Moment } from 'moment';

import {
  HotelAvailabilityStatusType,
  RoomCancellationPolicyDetailViewModel,
  RoomFeatureViewModel,
  RoomImageViewModel,
  RoomLowestPriceViewModel,
  RoomVariationViewModel,
  RoomViewModel,
} from '@api/index';
import ShowMoreCard, { ArrowAlign } from '@components/cards/ShowMoreCard';
import { Currencies } from '@constants/constants';
import { getCurrencySymbol } from '@utils/utils';
import useSearchBar from '@contexts/searchbar';

import { SelectedVariationProps } from '../../context/types';
import {
  useHotelDetailState,
  useSetSelectedVariations,
  useSetUnSelectedRooms,
} from '../../context';
import { ModalView } from '../details-galleries-modal';
import PriceSheet from '../price-sheet/PriceSheet';
import { ContainerRoomDetail, RoomColumn, RoomWrapper, Title } from './styled';
import { Entity } from '../details-galleries-modal/types';
import { RoomOptionDetailProps } from './RoomOptionDetails';
import { HotelRooms } from '../../HotelDetail';
import DetailedRoom from '../room-information/DetailedRoom';
import { getLowestRoomOption } from '../../helpers';
import Searchbar from './Searchbar';
import SelectedVariation from './SelectedVariation';

export interface DetailedRoomProps {
  type?: string;
  description?: string;
  roomTypeName?: string;
  roomId?: number;
  images?: RoomImageViewModel[];
  features?: RoomFeatureViewModel[];
  hasCancellationHour?: boolean;
  showRoom: (view: ModalView) => void;
  roomInformation?: string;
  prices?: RoomLowestPriceViewModel;
  lowestRoomOption?: RoomVariationViewModel;
  status?: HotelAvailabilityStatusType;
  isRefundable?: boolean;
  cancellationPolicyDetails?: RoomCancellationPolicyDetailViewModel[];
}

interface Props {
  items: HotelRooms[];
  hotelId: number;
  hotelName: string;
  countryCode?: string;
  nightCount: number;
  roomNumber: number;
  viewRoom: (
    room: HotelRooms,
    view: ModalView,
    entityType?: Entity,
    roomOptionDetailProps?: RoomOptionDetailProps
  ) => void;
  openDatePicker: () => void;
  isExpedia: boolean;
}

const RoomOptions = ({
  items,
  hotelId,
  hotelName,
  countryCode,
  nightCount,
  roomNumber = 1,
  viewRoom,
  openDatePicker,
  isExpedia,
}: Props) => {
  const { selectedVariations, unSelectedRooms } = useHotelDetailState();
  const setSelectedVariations = useSetSelectedVariations();

  const setUnSelectedRooms = useSetUnSelectedRooms();

  const {
    state: { hotel: state },
  } = useSearchBar();

  const [isPriceSheetVisible, setIsPriceSheetVisible] = useState(false);

  useEffect(() => {
    const activeTabs = new Array(roomNumber)
      .join()
      .split(',')
      .map((_, index) => ++index);

    const selectedRooms: number[] = selectedVariations.map(
      (selected) => selected.tabNumber
    );

    const unSelectedTabs = activeTabs.filter(
      (tab) => !selectedRooms.includes(tab)
    );
    setUnSelectedRooms(unSelectedTabs);
  }, [selectedVariations]);

  const isSelectedRoom = (tabNumber: number) =>
    selectedVariations.some((selected) => selected.tabNumber === tabNumber);

  const handleSelectVariation = (
    variation: SelectedVariationProps,
    room: { name?: string; detail?: string; image?: string }
  ): void => {
    if (isSelectedRoom(variation.tabNumber)) {
      setSelectedVariations([
        ...selectedVariations.filter(
          (selected) => selected.tabNumber !== variation.tabNumber
        ),
        { ...variation, room: room },
      ]);
    } else {
      setSelectedVariations([
        ...selectedVariations,
        { ...variation, room: room },
      ]);
    }
  };

  const onClosePriceSheet = () => {
    setIsPriceSheetVisible(false);
  };

  const roomOptionDetailProps = (
    room: RoomViewModel,
    selectedTab: number
  ): RoomOptionDetailProps => {
    return {
      room: room,
      hotelId: hotelId,
      isExpedia: isExpedia,
      hotelName: hotelName,
      countryCode: countryCode,
      openDatePicker,
      nightCount,
      startDate: state?.checkDates?.startDate as Moment,
      endDate: state?.checkDates?.endDate as Moment,
      hotelRoomList: state.rooms,
      selectedTab: selectedTab,
      selectedVariations: selectedVariations,
      handleSelectVariation: (v) =>
        handleSelectVariation(v, {
          name: room.name,
          detail:
            room?.details?.[0]?.roomInfo?.cancellationPolicyDetails?.[0]
              ?.description,
          image: room?.images?.[0]?.url,
        }),
    };
  };

  const getGuestInformation = useCallback(
    (room: RoomViewModel, roomIndex: number) => {
      const r = room.details
        ?.flatMap((detail) => detail.informations)
        ?.find((information) => information.requestedRoomIndex === roomIndex);
      return `${nightCount ? `${nightCount} Gece` : ''}${
        r?.adultCount ? `, ${r.adultCount} Yetişkin` : ''
      }${r?.childCount ? `, ${r.childCount} Çocuk` : ''}`;
    },
    [nightCount]
  );

  return (
    <ContainerRoomDetail>
      <ShowMoreCard
        title='Odalar'
        navigationText='Tüm Sezon Fiyatlarını Göster'
        isShowAll
        arrowAlign={ArrowAlign.FIXED_RIGHT}
      >
        <Searchbar />
        {selectedVariations
          ?.sort((a, b) => (a.tabNumber > b.tabNumber ? 1 : -1))
          .map((variation) => (
            <SelectedVariation
              key={variation.variationId}
              variation={variation}
            />
          ))}
        {unSelectedRooms?.slice(0, 6).map((tabNumber) => (
          <div key={tabNumber}>
            {roomNumber > 1 ? <Title>{tabNumber}. Odanızı Seçin</Title> : null}
            <RoomWrapper>
              {items.map((item, i) => (
                <RoomColumn key={i}>
                  <DetailedRoom
                    type={`${item?.name} ${
                      item?.typeName ? ` - ${item?.typeName}` : ''
                    }`}
                    description={item?.description || ''}
                    roomTypeName={item?.typeName || ''}
                    isRefundable={item?.details?.[0]?.isRefundable}
                    cancellationPolicyDetails={
                      item?.details?.[0]?.roomInfo?.cancellationPolicyDetails
                    }
                    images={item?.images ?? []}
                    features={item?.features || []}
                    hasCancellationHour={Boolean(!isExpedia)}
                    showRoom={(view: ModalView) => {
                      viewRoom(
                        item,
                        view,
                        'roomOptions',
                        roomOptionDetailProps(item, tabNumber)
                      );
                    }}
                    roomId={item?.id}
                    roomInformation={getGuestInformation(item, tabNumber)}
                    lowestRoomOption={getLowestRoomOption(item, tabNumber)}
                    status={
                      item.details
                        ?.flatMap((detail) => detail.informations)
                        ?.find(
                          (information) =>
                            information.requestedRoomIndex === tabNumber
                        )?.availabilityStatus
                    }
                  />
                </RoomColumn>
              ))}
            </RoomWrapper>
          </div>
        ))}
      </ShowMoreCard>
      {isPriceSheetVisible ? (
        <PriceSheet
          currency={getCurrencySymbol(Currencies.TL)}
          hotelId={hotelId}
          hotelName={hotelName}
          onCancel={onClosePriceSheet}
        />
      ) : null}
    </ContainerRoomDetail>
  );
};

export default RoomOptions;
